import { MovementPhase } from './movementData'
// Medical node status types
export type MedicalNodeStatus = 'green' | 'amber' | 'red' | 'black'
// Medical node type
export type MedicalNodeType = 'role1' | 'role2' | 'role2plus' | 'role3'
// Interface for medical nodes
export interface MedicalNode {
  id: string
  name: string
  label: string // M1, M2, etc.
  type: MedicalNodeType
  coordinates: [number, number] // [latitude, longitude]
  description: string
  distanceFromRoute: string
  capabilities: string[]
  bedCapacity: {
    total: number
    trauma: number
    icu: number
    general: number
    holding: number
  }
  staff: string[]
  supportRole: string
  status: MedicalNodeStatus
  occupancyPercentage: number
  timeline: {
    start: number // day number
    end: number // day number
  }
  evacuationRoute?: string
}
// Interface for medical evacuation routes
export interface MedicalEvacRoute {
  id: string
  name: string
  source: string // medical node ID
  destination: string // medical node ID
  coordinates: [number, number][] // Array of coordinate pairs forming a route
  description: string
  transportType: 'ground' | 'air' | 'both'
  estimatedTimeMinutes: number
}
// Interface for daily medical status
export interface DailyMedicalStatus {
  day: number
  nodeId: string
  bedsAvailable: number
  bedsOccupied: number
  status: MedicalNodeStatus
  notes: string
}
// Status colors for visual representation
export const medicalStatusColors: Record<MedicalNodeStatus, string> = {
  green: '#059669', // Green
  amber: '#D97706', // Amber
  red: '#DC2626', // Red
  black: '#1F2937', // Black
}
// Medical node type names
export const medicalNodeTypeNames: Record<MedicalNodeType, string> = {
  role1: 'Role I - Basic Aid Station',
  role2: 'Role II - Forward Surgical',
  role2plus: 'Role II+ - Enhanced Surgical',
  role3: 'Role III - Field Hospital',
}
// Medical nodes data
export const medicalNodes: MedicalNode[] = [
  {
    id: 'm1-del-rio',
    name: 'Del Rio Role II+ Aid Station',
    label: 'M1',
    type: 'role2plus',
    coordinates: [29.3627, -100.8968], // Del Rio
    description:
      'Role II+ Aid Station with stabilization and resuscitative surgery capabilities',
    distanceFromRoute: '<10 miles',
    capabilities: [
      'Stabilization',
      'Resuscitative surgery',
      'Trauma treatment',
      'EVAC triage staging area',
    ],
    bedCapacity: {
      total: 20,
      trauma: 3,
      icu: 0,
      general: 0,
      holding: 17,
    },
    staff: ['2 trauma medics', '1 PA', '1 surgical tech'],
    supportRole: 'Primary D+1–D+4 coverage for mid-route incidents',
    status: 'green',
    occupancyPercentage: 0, // Initially empty
    timeline: {
      start: 1,
      end: 4,
    },
    evacuationRoute: 'US-277 → Laredo',
  },
  {
    id: 'm2-laredo',
    name: 'Laredo Role III Medical Center',
    label: 'M2',
    type: 'role3',
    coordinates: [27.5306, -99.4803], // Laredo
    description:
      'Role III Medical Center with full surgical teams, labs, and ICU beds',
    distanceFromRoute: 'On route',
    capabilities: [
      'Full surgical teams',
      'Labs',
      'ICU beds',
      'CASEVAC receiving',
    ],
    bedCapacity: {
      total: 40,
      trauma: 0,
      icu: 10,
      general: 30,
      holding: 0,
    },
    staff: ['Surgical teams', 'ICU staff', 'General medical staff'],
    supportRole: 'Primary D+4–D+7 coverage, final stabilization or long hold',
    status: 'green',
    occupancyPercentage: 10, // 10% initially occupied
    timeline: {
      start: 4,
      end: 7,
    },
  },
]
// Medical evacuation routes
export const medicalEvacRoutes: MedicalEvacRoute[] = [
  {
    id: 'evac-m1-to-m2',
    name: 'Del Rio to Laredo EVAC Route',
    source: 'm1-del-rio',
    destination: 'm2-laredo',
    coordinates: [
      [29.3627, -100.8968], // Del Rio
      [28.7091, -100.4995], // Eagle Pass (intermediate point)
      [27.5306, -99.4803], // Laredo
    ],
    description: 'Primary evacuation route from Del Rio to Laredo',
    transportType: 'ground',
    estimatedTimeMinutes: 180, // 3 hours
  },
  {
    id: 'casevac-route',
    name: 'CASEVAC Air Route',
    source: 'm1-del-rio',
    destination: 'm2-laredo',
    coordinates: [
      [29.3627, -100.8968], // Del Rio
      [27.5306, -99.4803], // Laredo
    ],
    description: 'Air CASEVAC route for critical patients',
    transportType: 'air',
    estimatedTimeMinutes: 45, // 45 minutes
  },
]
// Daily medical status data
export const dailyMedicalStatus: DailyMedicalStatus[] = [
  // Del Rio (M1) status by day
  {
    day: 1,
    nodeId: 'm1-del-rio',
    bedsAvailable: 20,
    bedsOccupied: 0,
    status: 'green',
    notes: 'Fully operational, no patients',
  },
  {
    day: 2,
    nodeId: 'm1-del-rio',
    bedsAvailable: 17,
    bedsOccupied: 3,
    status: 'green',
    notes: 'Routine cases only, capacity normal',
  },
  {
    day: 3,
    nodeId: 'm1-del-rio',
    bedsAvailable: 12,
    bedsOccupied: 8,
    status: 'green',
    notes: 'Multiple cases, still good capacity',
  },
  {
    day: 4,
    nodeId: 'm1-del-rio',
    bedsAvailable: 5,
    bedsOccupied: 15,
    status: 'amber',
    notes: 'Approaching capacity, some transfers needed',
  },
  // Laredo (M2) status by day
  {
    day: 4,
    nodeId: 'm2-laredo',
    bedsAvailable: 36,
    bedsOccupied: 4,
    status: 'green',
    notes: 'Initial operations, preparing for transfers',
  },
  {
    day: 5,
    nodeId: 'm2-laredo',
    bedsAvailable: 30,
    bedsOccupied: 10,
    status: 'green',
    notes: 'Receiving transfers from Del Rio',
  },
  {
    day: 6,
    nodeId: 'm2-laredo',
    bedsAvailable: 25,
    bedsOccupied: 15,
    status: 'green',
    notes: 'Normal operations, capacity good',
  },
  {
    day: 7,
    nodeId: 'm2-laredo',
    bedsAvailable: 20,
    bedsOccupied: 20,
    status: 'amber',
    notes: 'Half capacity reached, monitoring closely',
  },
]
// Helper function to get medical node by ID
export const getMedicalNodeById = (id: string): MedicalNode | undefined => {
  return medicalNodes.find((node) => node.id === id)
}
// Helper function to get medical status for a specific day and node
export const getMedicalStatusForDayAndNode = (
  day: number,
  nodeId: string,
): DailyMedicalStatus | undefined => {
  return dailyMedicalStatus.find(
    (status) => status.day === day && status.nodeId === nodeId,
  )
}
// Helper function to convert time point to day number
export const timePointToDay = (timePoint: string): number => {
  switch (timePoint) {
    case '24h':
      return 1
    case '48h':
      return 2
    case '72h':
      return 3
    case '96h':
      return 4
    case '5d':
      return 5
    case '6d':
      return 6
    case '7d':
      return 7
    default:
      return 0 // For "now", show day 0 (not started)
  }
}
